// @services/clients/ListingInferenceReviewPortalClient.ts
import { v7 as uuidv7 } from "uuid";
import type { TaskEitherTs } from "@ps-bridge/interop";
import type {
    ListingInferenceApiDecodeResult,
    ListingInferenceEvidenceQuery,
    ListingInferenceEvidenceQueryResult,
    ListingInferenceJsonPayload,
    ListingInferenceManualDecisionRequest,
    ListingInferenceManualDecisionResult,
    ListingInferenceReviewErrorCode,
    ListingInferenceReviewError,
    ListingInferenceWorkspaceReadResult,
} from "@services/clients/ListingInferenceReviewPortalApi";
import {
    decodeListingInferenceEvidenceQueryResult,
    decodeListingInferenceManualDecisionResult,
    decodeListingInferenceRefusal,
    decodeListingInferenceWorkspaceReadResult,
    encodeListingInferenceEvidenceQueryParams,
    encodeListingInferenceManualDecisionRequest,
    ListingInferenceReviewPortalContract,
} from "@services/clients/ListingInferenceReviewPortalApi";
import type { ListingInferenceListingId } from "@ps-bridge/models/brands/listingInferenceReviewPortal";
import API_URL from "@utils/config";
import { taskEitherChain, taskEitherLeft, taskEitherOf, taskEitherTryCatch } from "@utils/effects/branded";
import * as Option from "@utils/Option";

export interface ListingInferenceReviewPortalClient {
    readWorkspace(
        listingId: ListingInferenceListingId,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceWorkspaceReadResult>;

    queryEvidence(
        query: ListingInferenceEvidenceQuery,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceEvidenceQueryResult>;

    submitDecision(
        request: ListingInferenceManualDecisionRequest,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceManualDecisionResult>;
}

type ListingInferenceResponseDecoder<T> = (payload: ListingInferenceJsonPayload) => ListingInferenceApiDecodeResult<T>;

type ListingInferenceGetRequest<T> = {
    readonly route: string;
    readonly method: "GET";
    readonly decoder: ListingInferenceResponseDecoder<T>;
};

type ListingInferencePostRequest<T> = {
    readonly route: string;
    readonly method: "POST";
    readonly body: ListingInferenceJsonPayload;
    readonly decoder: ListingInferenceResponseDecoder<T>;
};

type ListingInferenceFetchRequest<T> = ListingInferenceGetRequest<T> | ListingInferencePostRequest<T>;

type ListingInferenceStatusContext = "read" | "evidence" | "decision";
type ListingInferenceStatusFallback = {
    readonly context: ListingInferenceStatusContext;
    readonly unsafeFactPromotion: boolean;
};
type ListingInferenceStatusErrorCode = Exclude<
    ListingInferenceReviewErrorCode,
    "networkError" | "responseDecodeError"
>;

const networkError = (): ListingInferenceReviewError => ({
    error: "networkError",
    message: "The listing inference review portal service could not be reached.",
    trace: Option.none(),
});

const responseDecodeError = (
    message = "Unable to decode listing inference review portal response.",
): ListingInferenceReviewError => ({
    error: "responseDecodeError",
    message,
    trace: Option.none(),
});

const routeForListing = (route: string, listingId: ListingInferenceListingId): string =>
    route.replace("{listingId}", encodeURIComponent(listingId));

const requestTrace = (traceId: string) => ({
    traceId,
    requestId: `frontend-${traceId}`,
});

const statusErrorCode = (
    status: number,
    fallback: ListingInferenceStatusFallback,
): ListingInferenceStatusErrorCode => {
    const { context } = fallback;
    if (context === "decision") {
        if (status === 401 || status === 403) {
            return "writeDenied";
        }
        if (status === 422) {
            return fallback.unsafeFactPromotion ? "unsafeFactPromotionRefused" : "invalidDecision";
        }
        if (status >= 400 && status < 500) {
            return "invalidDecision";
        }
        return "serviceUnavailable";
    }

    if (status === 400) {
        return "invalidListingId";
    }
    if (status === 401 || status === 403) {
        return "readDenied";
    }
    if (status === 404) {
        return "workspaceNotFound";
    }
    if (context === "evidence" && status === 422) {
        return "unsafeFactPromotionRefused";
    }
    return "serviceUnavailable";
};

const statusErrorMessage = (error: ListingInferenceStatusErrorCode): string => {
    switch (error) {
        case "invalidListingId":
            return "Listing inference review request used an invalid listing id.";
        case "workspaceNotFound":
            return "Listing inference review workspace was not found.";
        case "readDenied":
            return "Listing inference review workspace read permission was denied.";
        case "writeDenied":
            return "Listing inference review decision write permission was denied.";
        case "invalidDecision":
            return "Listing inference review decision request was invalid.";
        case "unsafeFactPromotionRefused":
            return "Unsafe fact promotion was refused for listing inference review.";
        case "serviceUnavailable":
            return "The listing inference review portal service is unavailable.";
    }
};

const fallbackStatusError = (
    status: number,
    fallback: ListingInferenceStatusFallback,
    traceId: string,
): ListingInferenceReviewError => {
    const error = statusErrorCode(status, fallback);
    const decoded = decodeListingInferenceRefusal({
        error,
        message: statusErrorMessage(error),
        trace: requestTrace(traceId),
    });

    return decoded.type === "ok" ? decoded.value : responseDecodeError(decoded.message);
};

const errorFromResponseBody = (
    status: number,
    fallback: ListingInferenceStatusFallback,
    traceId: string,
    bodyText: string,
): ListingInferenceReviewError => {
    if (bodyText.trim().length === 0) {
        return fallbackStatusError(status, fallback, traceId);
    }

    try {
        const decoded = decodeListingInferenceRefusal(JSON.parse(bodyText));
        return decoded.type === "ok" ? decoded.value : fallbackStatusError(status, fallback, traceId);
    } catch {
        return fallbackStatusError(status, fallback, traceId);
    }
};

const decodeOrLeft = <T>(
    result: ListingInferenceApiDecodeResult<T>,
): TaskEitherTs<ListingInferenceReviewError, T> => {
    if (result.type === "ok") {
        return taskEitherOf(result.value);
    }

    return taskEitherLeft(responseDecodeError(result.message));
};

const readResponseJson = async (response: Response): Promise<ListingInferenceJsonPayload> => response.json();
const readResponseText = async (response: Response): Promise<string> => response.text();

const mapFetchResponse = <T>(
    response: Response,
    decoder: ListingInferenceResponseDecoder<T>,
    fallback: ListingInferenceStatusFallback,
    traceId: string,
): TaskEitherTs<ListingInferenceReviewError, T> => {
    if (!response.ok) {
        const bodyTask = taskEitherTryCatch(
            () => readResponseText(response),
            () => responseDecodeError("Unable to read listing inference review refusal response."),
        );

        return taskEitherChain(bodyTask, (bodyText) =>
            taskEitherLeft(errorFromResponseBody(response.status, fallback, traceId, bodyText)),
        );
    }

    const payloadTask = taskEitherTryCatch(
        () => readResponseJson(response),
        () => responseDecodeError(),
    );

    return taskEitherChain(payloadTask, (payload) => decodeOrLeft(decoder(payload)));
};

const fetchListingInference = <T>(
    request: ListingInferenceFetchRequest<T>,
    fallback: ListingInferenceStatusFallback,
): TaskEitherTs<ListingInferenceReviewError, T> => {
    const traceId = uuidv7();
    const headers: Record<string, string> = {
        Accept: "application/json",
        "X-Trace-ID": traceId,
    };
    const requestInit: RequestInit = {
        method: request.method,
        credentials: "include",
        headers,
    };

    if ("body" in request) {
        headers["Content-Type"] = "application/json";
        requestInit.body = JSON.stringify(request.body);
    }

    const fetchTask = taskEitherTryCatch(
        () => fetch(`${API_URL}${request.route}`, requestInit),
        () => networkError(),
    );

    return taskEitherChain(fetchTask, (response) =>
        mapFetchResponse(response, request.decoder, fallback, traceId),
    );
};

export class DefaultListingInferenceReviewPortalClient implements ListingInferenceReviewPortalClient {
    readWorkspace(
        listingId: ListingInferenceListingId,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceWorkspaceReadResult> {
        return fetchListingInference(
            {
                route: routeForListing(ListingInferenceReviewPortalContract.reviewWorkspacePath, listingId),
                method: "GET",
                decoder: decodeListingInferenceWorkspaceReadResult,
            },
            {
                context: "read",
                unsafeFactPromotion: false,
            },
        );
    }

    queryEvidence(
        query: ListingInferenceEvidenceQuery,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceEvidenceQueryResult> {
        const route = routeForListing(ListingInferenceReviewPortalContract.evidencePath, query.listingId);
        const params = encodeListingInferenceEvidenceQueryParams(query);

        return fetchListingInference(
            {
                route: `${route}?${params.toString()}`,
                method: "GET",
                decoder: decodeListingInferenceEvidenceQueryResult,
            },
            {
                context: "evidence",
                unsafeFactPromotion: query.unsafeFactPromotion,
            },
        );
    }

    submitDecision(
        request: ListingInferenceManualDecisionRequest,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceManualDecisionResult> {
        return fetchListingInference(
            {
                route: routeForListing(ListingInferenceReviewPortalContract.reviewDecisionsPath, request.listingId),
                method: "POST",
                body: encodeListingInferenceManualDecisionRequest(request),
                decoder: decodeListingInferenceManualDecisionResult,
            },
            {
                context: "decision",
                unsafeFactPromotion: request.unsafeFactPromotion,
            },
        );
    }
}
